import { Tags } from 'jsmediatags/types';
import formatDuration from '../../utilities/formatDuration';
import { TrackProperty } from '../../services/columnSettings/playlistColumns';
import Source, { Sources } from '../source';
import TrackLength from './trackLength';

export default class Track {
  /**
   * We don't use class instance methods so we can
   * skip recreating all library json as track objects on load
   * @param val
   * @param trackProp
   */
  static format(val: any, trackProp: TrackProperty, track?: Track) {
    switch (trackProp) {
      /**
       * Get Date obj from a Track date property (releaseDate, dateAdded)
       * Handles values as string and numeric
       *
       * @param date Set to any to remind of the shift in date storage
       * Dates where being JSON.stringified to string values but then changed to numeric valueOf
       */
      case TrackProperty.releaseDate:
      case TrackProperty.dateAdded:
        if (val) {
          return new Date(val).toLocaleDateString();
        }
        break;

      case TrackProperty.length:
        switch (val) {
          case TrackLength.NOT_READY:
            return 'Not Ready';
          case TrackLength.UNAVAILABLE:
            return '';

          default:
            return formatDuration(val, true);
        }

      case TrackProperty.type:
        return Sources[val as Source].name;

      // "320 kbps", "245 kbps VBR". The unit is in the cell rather than the
      // header because the header says "Bit Rate", and a VBR average reads
      // differently from a constant rate.
      case TrackProperty.bitrate:
        if (val === undefined || val === null) return undefined;

        return `${val} kbps${track?.vbr ? ' VBR' : ''}`;

      default:
        return val;
    }
  }

  missing = false;

  /**
   * Tag-derived fields beyond the ones the constructor takes.
   *
   * All optional and all left undefined until a tag read fills them, so a
   * track that has never been read costs nothing in the library file -
   * JSON.stringify drops undefined. They are plain data with no instance
   * methods, so they survive the JSON round trip (ARCHITECTURE.md section 2).
   *
   * Only trackNumber has a column. The rest are searchable by prefix
   * (albumartist:, composer:, comment:, disc:, bpm:, key:, publisher:).
   */

  /** TPE2 / TP2 / aART. What separates a compilation from its performers. */
  albumArtist?: string;
  /** TCOM / TCM / ©wrt. */
  composer?: string;
  /** COMM / COM / ©cmt - the free text half of the frame. */
  comment?: string;
  /**
   * TRCK / TRK / trkn, as a number.
   *
   * The frame is often "3/12". Only the position is kept: the total is
   * derivable from the album, and a string would sort "10" before "2".
   */
  trackNumber?: number;
  /** TPOS / TPA / disk, same treatment as trackNumber. */
  disc?: number;
  /** TBPM / TBP / tmpo. */
  bpm?: number;
  /** TKEY / TKE, f.x. "Am". */
  initialKey?: string;
  /** TPUB / TPB - the label. */
  publisher?: string;

  /**
   * Fields the user has set by hand in the track editor. A tag read leaves
   * every marked field alone, so "Refetch Tags" fills in blanks without
   * undoing a correction. Absent until the first edit, so an untouched
   * library costs nothing for it. Written only through `utilities/trackEdits`.
   */
  userEdited?: Partial<
    Record<
      | 'title'
      | 'artist'
      | 'album'
      | 'albumArtist'
      | 'genre'
      | 'releaseDate'
      | 'trackNumber'
      | 'disc'
      | 'composer'
      | 'comment'
      | 'bpm'
      | 'initialKey'
      | 'publisher',
      true
    >
  >;

  /**
   * The account a track was posted from: a YouTube channel, a SoundCloud or
   * Mixcloud uploader. Not a tag - the source fills this one in - and not the
   * artist: a set by one DJ is often posted by a label or a station, and a
   * YouTube track has no artist at all.
   *
   * Optional like the tag fields above and for the same reason: a file has no
   * uploader, and JSON.stringify drops undefined, so the 39k Drive tracks in a
   * real library cost nothing for it. Hidden by default in every scope that
   * offers it - see the sentinel note in playlistColumns.ts.
   */
  uploader?: string;

  /**
   * What the file is, as opposed to what its tags say - the two fields the
   * Format and Bit Rate columns show. File sources only; a stream has
   * neither.
   *
   * Optional and absent until known, like the tag fields, so a library from
   * before they existed costs nothing for them and shows blanks until a
   * track is added or refetched.
   */

  /**
   * The file's extension, lower case, without the dot: "mp3", "m4a",
   * "aiff". From the file name the drive reports, on add and on every
   * refetch - see models/fileExtensions.ts fileFormat.
   */
  format?: string;
  /**
   * Whole kilobits per second, from the file's own headers
   * (utilities/audioProperties.ts) or OneDrive's audio facet. A variable
   * bit rate encode carries its average here and `vbr` beside it.
   */
  bitrate?: number;
  /** Only ever `true`, and only on a variable bit rate encode; absent otherwise. */
  vbr?: true;

  /**
   *
   * @param id Library id / OneDrive id / YouTube Video id
   * @param type @type Source
   * @param title
   * @param artist
   * @param album
   * @param length Length in ms
   * @param genre
   * @param releaseDate
   * @param dateAdded Date added to library
   * @param tags F.x. ID3
   */
  constructor(
    public id: string,
    public type: Source,
    public title = 'Title Missing',
    public artist = '',
    public album = '',
    public length = TrackLength.UNAVAILABLE as number,
    public genre = '',
    public releaseDate = 0,
    public dateAdded = new Date().valueOf(),
    public tags: Tags = {},
  ) {}

  /**
   * Implemented by sub-classes
   */
  dispose() {}

  // toJSON: () => any = () => ({
  //   type: this.type,
  //   id: this.id,
  // });
}
